function renderGenDeleteContentServerActionTemplate({
  ctxTableVar,
  ctxCacheInvalidationFn,
  ctxCamelPlural,
  lifecycleHookImport,
  ctxSingular,
  deleteContext,
  beforeHook,
  ctxPlural,
  afterHook,
  ctxSingularText
}: {
  ctxTableVar: string
  ctxCacheInvalidationFn: string
  ctxCamelPlural: string
  lifecycleHookImport: string
  ctxSingular: string
  deleteContext: string
  beforeHook: string
  ctxPlural: string
  afterHook: string
  ctxSingularText: string
}): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctxTableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { deleteEntityVersions, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctxCamelPlural}CacheTags } from './types'
${lifecycleHookImport}
import type { ${ctxSingular}DeleteResult } from './types'

export async function delete${ctxSingular}(id: string): Promise<${ctxSingular}DeleteResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])
  const now = new Date().toISOString()
  const auditRequestMetadata = await getAuditRequestMetadata()
  const lifecycleContext = ${deleteContext}
  ${beforeHook}

  try {
    const [deletedRow] = await db.transaction(async (tx) => {
      await deleteEntityVersions(tx, '${ctxPlural}', [id])
      return tx.delete(${ctxTableVar}).where(eq(${ctxTableVar}.id, id)).returning()
    })
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.all)
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.byId(id))
    ${ctxCacheInvalidationFn}(entityVersionsCacheTags.for('${ctxPlural}', id))
    ${afterHook}
    return { success: true }
  } catch (error) {
    console.error('Error deleting ${ctxSingularText}:', error)
    return { success: false, error: error instanceof Error ? error.message : 'Failed to delete ${ctxSingularText}' }
  }
}
`
}

function renderGenDeleteContentTemplate(): string {
  return `{
      ...lifecycleContext,
      data: deletedRow as unknown as Record<string, unknown> | undefined
    }`
}

export function renderGenDeleteContentRenderer(
  request:
    | {
        kind: 'renderGenDeleteContentServerActionTemplate'
        props: {
          ctxTableVar: string
          ctxCacheInvalidationFn: string
          ctxCamelPlural: string
          lifecycleHookImport: string
          ctxSingular: string
          deleteContext: string
          beforeHook: string
          ctxPlural: string
          afterHook: string
          ctxSingularText: string
        }
      }
    | { kind: 'renderGenDeleteContentTemplate' }
): string {
  switch (request.kind) {
    case 'renderGenDeleteContentServerActionTemplate':
      return renderGenDeleteContentServerActionTemplate(request.props)
    case 'renderGenDeleteContentTemplate':
      return renderGenDeleteContentTemplate()
  }

  throw new Error('Unsupported renderer request')
}
